import type { InstanceTab } from "./instanceDetailTabs";

export type AttentionTone = "crit" | "warn";

export type AttentionItem = {
  readonly key: string;
  readonly tone: AttentionTone;
  readonly text: string;
  readonly tab: InstanceTab;
};

export type AttentionInput = {
  readonly diskPct: number | null;
  readonly swapPct: number | null;
  readonly uptimePct: number | null;
  readonly unexpectedRestarts: number | null;
  readonly oomEvents: number | null;
  readonly toolOk: number | null;
  readonly toolError: number | null;
  readonly windowLabel: string;
};

// Same swap level the Swap chart marks: fleet baseline is 0-15%.
const SWAP_WARN_PCT = 50;
const SWAP_CRIT_PCT = 80;
const UPTIME_WARN_PCT = 95;
const TOOL_ERROR_WARN_RATE = 0.05;

const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

const diskItem = (pct: number | null): AttentionItem | null =>
  pct == null || pct < 80
    ? null
    : { key: "disk", tone: pct >= 90 ? "crit" : "warn", text: `Disk ${pct}% full`, tab: "health" };

const restartItems = (input: AttentionInput): AttentionItem[] => {
  const items: AttentionItem[] = [];
  if ((input.unexpectedRestarts ?? 0) > 0) {
    const n = input.unexpectedRestarts ?? 0;
    items.push({ key: "restarts", tone: "warn", text: `${plural(n, "unexpected restart", "unexpected restarts")} ${input.windowLabel}`, tab: "health" });
  }
  if ((input.oomEvents ?? 0) > 0) {
    const n = input.oomEvents ?? 0;
    items.push({ key: "oom", tone: "crit", text: `${plural(n, "OOM event", "OOM events")} ${input.windowLabel}`, tab: "health" });
  }
  return items;
};

const toolItem = (ok: number | null, error: number | null, windowLabel: string): AttentionItem | null => {
  const total = (ok ?? 0) + (error ?? 0);
  if (error == null || total === 0 || error / total < TOOL_ERROR_WARN_RATE) return null;
  const pct = Math.round((error / total) * 100);
  return { key: "tools", tone: "warn", text: `${error} failed tool calls (${pct}%) ${windowLabel}`, tab: "activity" };
};

// Crit first, then warn; order within a tone follows the checks below.
export const attentionItems = (input: AttentionInput): AttentionItem[] => {
  const items: Array<AttentionItem | null> = [
    diskItem(input.diskPct),
    input.swapPct != null && input.swapPct >= SWAP_WARN_PCT
      ? { key: "swap", tone: input.swapPct >= SWAP_CRIT_PCT ? "crit" : "warn", text: `Swap at ${Math.round(input.swapPct)}%`, tab: "health" }
      : null,
    input.uptimePct != null && input.uptimePct < UPTIME_WARN_PCT
      ? { key: "uptime", tone: "warn", text: `Uptime ${input.uptimePct.toFixed(1)}% ${input.windowLabel}`, tab: "health" }
      : null,
    ...restartItems(input),
    toolItem(input.toolOk, input.toolError, input.windowLabel),
  ];
  const present = items.filter((item): item is AttentionItem => item !== null);
  return [...present.filter((i) => i.tone === "crit"), ...present.filter((i) => i.tone === "warn")];
};

export type SourceStatus = "loading" | "ready" | "error";

export type AttentionCoverage = "pending" | "partial" | "complete";

// A missing input reads as "no problem" above, so an empty list only means
// all-clear once every source has loaded. A failure is reported even while
// other sources are still loading.
export const attentionCoverage = (statuses: ReadonlyArray<SourceStatus>): AttentionCoverage =>
  statuses.includes("error") ? "partial" : statuses.includes("loading") ? "pending" : "complete";
